/**
 * The sync wire contract, shared verbatim by the PWA and the API.
 *
 * ADR-0001 §3.3 picked one language on both sides specifically so this file is imported
 * by client and server rather than reimplemented twice and allowed to drift. Anything
 * that crosses the network is defined here and nowhere else.
 *
 * Compatibility rules, because this outlives any single release of the app:
 *  - `PROTOCOL_VERSION` changes only when the envelope shape changes. Old clients in the
 *    field are not upgradable on demand — a crew on a roof with no signal is running
 *    whatever they installed last week, and their queued ops must still be acceptable.
 *  - `schemaVersion` rides on every op payload (ADR-0001 §6.1) so the server can apply an
 *    op written by an older client without guessing what the fields meant at the time.
 */

import type { Uuid } from './ids.js';

/** Envelope version. Bump only for breaking changes to the envelope itself. */
export const PROTOCOL_VERSION = 1;

/** Payload schema version stamped onto every op this build produces. */
export const CURRENT_SCHEMA_VERSION = 1;

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export type JsonObject = { [key: string]: JsonValue };

/**
 * Entities that sync. Photos are here as metadata only — the bytes take the presigned
 * upload path in ADR-0001 §4.6 and never travel through this protocol.
 */
export type EntityType = 'customer' | 'job' | 'photo' | 'measurement' | 'note';

/**
 * Op verbs. Deliberately small, and deliberately without `delete`.
 *
 * ADR-0001 §4.4: evidence is append-only. A correction is a `supersede` that points at
 * what it replaces and leaves the original readable in the audit view. A removal is a
 * `redact`, which sets a flag; the bytes and the prior record stay. There is no verb in
 * this protocol that destroys evidence, because destroying evidence is not a capability
 * the client is allowed to express.
 */
export type OpType = 'create' | 'update' | 'supersede' | 'redact';

/**
 * One immutable mutation, minted on the device.
 *
 * Every field here is set before the op is written to the local outbox, and none of them
 * are rewritten afterwards. Retrying an op resends these exact bytes.
 */
export interface SyncOp {
  /** UUIDv7, client-generated. The server's idempotency key (ADR-0001 §4.2 step 4). */
  readonly opId: Uuid;
  /** Tenant. Present from the first migration even though v1 has one org (ADR-0001 §6.4). */
  readonly orgId: Uuid;
  readonly entityType: EntityType;
  /** Client-generated UUIDv7. The client does not wait for a server id to keep working. */
  readonly entityId: Uuid;
  readonly opType: OpType;
  readonly payload: JsonObject;
  readonly schemaVersion: number;
  /**
   * What the crew's device believed the time was. Displayed as "captured at" because it
   * is what they experienced. Never used for ordering — ADR-0001 §4.5.
   */
  readonly clientCreatedAt: string;
  readonly deviceId: Uuid;
  readonly actorId: Uuid;
  /** For `supersede`: the record being corrected. Retained and still visible. */
  readonly supersedesId?: Uuid;
}

/**
 * A batch of ops to apply.
 *
 * **The server's ordering obligation, which is part of the contract and not an
 * implementation detail:**
 *
 *  1. Ops are applied in array order. The client sends them in per-entity FIFO order and
 *     relies on that order being preserved.
 *  2. If an op fails, every *later* op in the same batch for the *same `entityId`* must
 *     be rejected without being applied — `conflict`, retryable. Applying an `update`
 *     whose `create` was just rejected attaches evidence to a base that does not exist,
 *     and the client cannot undo a write the server confirmed.
 *  3. Ops for unrelated entities in the same batch are unaffected. One bad job must not
 *     stall a crew's whole queue.
 *
 * The client enforces the same rule across batches (it will not send an entity's later
 * ops while an earlier one is unresolved), but it cannot enforce it *within* a batch —
 * only the server can, so the server must.
 */
export interface PushOpsRequest {
  readonly protocolVersion: number;
  readonly ops: readonly SyncOp[];
}

/**
 * Per-op outcome.
 *
 * `duplicate` is a success, not an error: it means a previous attempt already landed and
 * the unique index on `op_id` caught the retry. This is the whole point of at-least-once
 * delivery with exactly-once effect — the client can retry forever on flaky LTE without
 * creating duplicate evidence.
 */
export type OpResult =
  | { readonly opId: Uuid; readonly status: 'applied'; readonly seq: number }
  | { readonly opId: Uuid; readonly status: 'duplicate'; readonly seq: number }
  | { readonly opId: Uuid; readonly status: 'rejected'; readonly error: OpError };

export interface OpError {
  readonly code: OpErrorCode;
  readonly message: string;
  /**
   * Whether resending the identical op could ever succeed.
   *
   * Retryable means the op is still good and the world was temporarily wrong. Terminal
   * means this op will never be accepted — it quarantines its entity rather than
   * spinning forever, so a crew sees a real error instead of a queue that never drains.
   */
  readonly retryable: boolean;
}

export type OpErrorCode =
  | 'validation_failed'
  | 'schema_version_unsupported'
  | 'entity_not_found'
  | 'forbidden'
  | 'org_mismatch'
  | 'conflict'
  | 'internal';

export interface PushOpsResponse {
  readonly protocolVersion: number;
  readonly results: readonly OpResult[];
}

/** One committed entry from the append-only log (ADR-0001 §4.7). */
export interface ChangeEntry {
  /** Ordering authority. Monotonic, server-assigned, never reused. */
  readonly seq: number;
  readonly opId: Uuid;
  readonly orgId: Uuid;
  readonly entityType: EntityType;
  readonly entityId: Uuid;
  readonly opType: OpType;
  readonly payload: JsonObject;
  readonly schemaVersion: number;
  readonly actorId: Uuid;
  readonly deviceId: Uuid;
  readonly clientCreatedAt: string;
  /** When the server actually received it. The honest timestamp. */
  readonly receivedAt: string;
}

export interface PullChangesRequest {
  /** Exclusive cursor. `0` means "from the beginning". */
  readonly since: number;
  readonly limit: number;
}

export interface PullChangesResponse {
  readonly protocolVersion: number;
  readonly changes: readonly ChangeEntry[];
  /** Cursor to pass as `since` next time. */
  readonly latestSeq: number;
  readonly hasMore: boolean;
}

/**
 * Terminal error codes, as a runtime value.
 *
 * Kept adjacent to the type so that adding a code forces a decision about whether it is
 * retryable, instead of defaulting to "retry forever" by omission.
 */
const TERMINAL_CODES: ReadonlySet<OpErrorCode> = new Set<OpErrorCode>([
  'validation_failed',
  'schema_version_unsupported',
  'forbidden',
  'org_mismatch',
]);

export function isTerminalErrorCode(code: OpErrorCode): boolean {
  return TERMINAL_CODES.has(code);
}
